import React from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import {
  Typography,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
} from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Autocomplete as AutoComplete } from '@material-ui/lab';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import CustomAccordion from 'containers/LawyerLegalProceedingsPage/CivilProceedingRecap/CustomAccordion';
import { get, isEmpty, uniqueId, isNil } from 'lodash';
import { CheckCircle, RadioButtonUnchecked } from '@material-ui/icons';
import messages from 'containers/PreOpeningPage/messages';
import { translate } from 'containers/services/translate';
import { Listings, SagisConfig } from 'containers/AppContainer/typings';
import victimIcon from 'images/victim-icon.svg';
import { VICTIM } from 'containers/data-structure';
import PrimaryButton from 'components/Buttons/PrimaryButton';

import {
  CHOICE,
  GENDER,
  INJURY_POST,
  MINOR_VALUE,
} from 'containers/PreOpeningPage/constants';
import { oppositionVictimValue } from 'containers/PreOpeningPage/utils';
import { COLORS } from 'constants/design';
import frLocale from 'date-fns/locale/fr';
import { filterVisibleOnly } from 'containers/services/filters';
import { getMoroccanCities, getCity } from 'containers/RecapManagerPage/utils';
import Autocomplete, {
  createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import { dateStringToLocalDate } from 'containers/IdentificationPage/utils';
import BeneficiaryAccordion from 'containers/PreOpeningPage/BeneficiaryAccordion';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { isOtherProfession } from 'containers/CourtManagerLegalProceedings/utils';
import { VICTIM_ITT_AVAILABILITY } from 'containers/PreOpeningPage/constants';
import {
  Container,
  StyledSelect,
} from 'containers/TransactionManagerTimeLine/VictimInfo/styles';
import { VictimType, BeneficiaryType } from 'typings/globalTypes';
import BeneficiaryDrawer from 'containers/TransactionManagerTimeLine/VictimInfo/BeneficiaryDrawer';
import { requestAddBeneficiary } from 'containers/TransactionManagerTimeLine/actions';
import currentUserStorage from 'containers/services/security/current-user-storage';

export interface VictimCardProps {
  intl: IntlShape;
  disableInput?: boolean;
  inputLabelProps?: boolean;
  listings: Listings;
  isReadOnly: boolean;
  victim: VictimType;
  inputProps?: any;
  couldAddBeneficiaries: boolean;
  lossDate: Date;
  claimId: number;
  isConsultation?: boolean;
}

const VictimCard: React.FC<VictimCardProps> = (props): JSX.Element => {
  const dispatch = useDispatch();
  const { victim } = props;
  const [
    openDrawerAddBeneficiary,
    setOpenDrawerAddBeneficiary,
  ] = React.useState(false);

  const [beneficiaryToAdd, setBeneficiaryToAdd] = React.useState({
    dependentIndicator: true,
  });

  const inputLabel = React.useRef<HTMLLabelElement>(null);
  const isExcludedValue = (isExcluded: boolean | string) => {
    if (typeof isExcluded === 'boolean') {
      return isExcluded ? CHOICE.YES : CHOICE.NO;
    }
    return isExcluded;
  };
  const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: (option: SagisConfig) => option?.ItemDescription,
  });
  const handleSuccess = () => {
    toast.success(translate(messages.beneficiarySuccessToast, props));
    setBeneficiaryToAdd({
      dependentIndicator: true,
    });
  };
  const handleSubmitAddBeneficiary = (payload: BeneficiaryType) => {
    dispatch(
      requestAddBeneficiary(
        {
          beneficiary: {
            ...payload,
            claimId: props.claimId,
            victimId: get(victim, 'id'),
            creator: currentUserStorage?.get()?.username,
          },
        } as any,
        handleSuccess,
      ),
    );
  };
  const handleToggleDrawer = () => {
    setOpenDrawerAddBeneficiary(false);
  };
  return (
    <Container className="container">
      <div className="victims">
        <div>
          <CustomAccordion
            title={translate({ ...messages.victimInfoTitle }, props)}
            textColor={COLORS.ACCORDION_TEXT_BLUE}
            bgColor={COLORS.ACCORDION_BG_BLUE}
            image={victimIcon}
          >
            <div className="victim-container">
              <Grid container direction="row" spacing={2}>
                <Grid item xs={3} className="grid-input">
                  <FormControl fullWidth>
                    <InputLabel
                      ref={inputLabel}
                      id="simple-select-outlined-label-procedure"
                      shrink={props.inputLabelProps}
                    >
                      {translate({ ...messages.victimInjuryPostKey }, props)}
                    </InputLabel>
                    <StyledSelect
                      name={VICTIM.INJURY_POST_KEY}
                      value={
                        get(victim, [VICTIM.INJURY_POST_KEY], '') ===
                        INJURY_POST.INJURED[1]
                          ? INJURY_POST.INJURED[0]
                          : get(victim, [VICTIM.INJURY_POST_KEY], '')
                      }
                      disabled={props.disableInput}
                    >
                      {!isEmpty(get(props.listings, 'prejudice', [])) &&
                        props
                          .listings!.prejudice.filter(
                            (el: any) => el.ItemKey !== INJURY_POST.INJURED[1],
                          )
                          .map((e: any) => (
                            <MenuItem value={e.ItemKey} key={e.ItemKey}>
                              {get(e.ItemDescription.split(' '), '[0]', '')}
                            </MenuItem>
                          ))}
                    </StyledSelect>
                  </FormControl>
                </Grid>
                <Grid item xs={3} className="grid-input">
                  <TextField
                    label={translate({ ...messages.victimCin }, props)}
                    fullWidth
                    name={VICTIM.CIN}
                    value={get(victim, [VICTIM.CIN])}
                    InputProps={{
                      ...props.inputProps,
                    }}
                    disabled={props.disableInput}
                    InputLabelProps={{
                      shrink: props.inputLabelProps,
                    }}
                  />
                </Grid>
                <Grid item xs={3} className="grid-input">
                  <TextField
                    label={translate({ ...messages.victimLastName }, props)}
                    fullWidth
                    name={VICTIM.LAST_NAME}
                    value={get(victim, [VICTIM.LAST_NAME])}
                    InputProps={props.inputProps}
                    disabled={props.disableInput}
                    InputLabelProps={{
                      shrink: props.inputLabelProps,
                    }}
                  />
                </Grid>
                <Grid item xs={3} className="grid-input">
                  <TextField
                    label={translate({ ...messages.victimFirstName }, props)}
                    InputProps={props.inputProps}
                    name={VICTIM.FIRST_NAME}
                    value={get(victim, [VICTIM.FIRST_NAME])}
                    disabled={props.disableInput}
                    InputLabelProps={{
                      shrink: props.inputLabelProps,
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={3} className="grid-input">
                  <MuiPickersUtilsProvider
                    utils={DateFnsUtils}
                    locale={frLocale}
                  >
                    <KeyboardDatePicker
                      autoOk
                      fullWidth
                      variant="inline"
                      format="dd/MM/yyyy"
                      id="date-picker-inline-victim-date-of-birth"
                      label={translate(
                        { ...messages.victimDateOfBirth },
                        props,
                      )}
                      name={VICTIM.DATE_OF_BIRTH}
                      value={dateStringToLocalDate(
                        victim[VICTIM.DATE_OF_BIRTH],
                      )}
                      InputProps={props.inputProps}
                      disabled={props.disableInput}
                      InputLabelProps={{
                        shrink: props.inputLabelProps,
                      }}
                      onChange={() => {}}
                    />
                  </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel
                      ref={inputLabel}
                      id="simple-select-outlined-label-procedure"
                      shrink={props.inputLabelProps}
                    >
                      {translate({ ...messages.sexe }, props)}
                    </InputLabel>
                    <StyledSelect
                      name={VICTIM.GENDER}
                      value={get(victim, [VICTIM.GENDER], '')}
                      disabled={props.disableInput}
                      labelWidth={230}
                    >
                      <MenuItem value={GENDER.MALE} key={0}>
                        {GENDER.MALE}
                      </MenuItem>
                      <MenuItem value={GENDER.FEMALE} key={1}>
                        {GENDER.FEMALE}
                      </MenuItem>
                    </StyledSelect>
                  </FormControl>
                </Grid>
                <Grid item xs={3} className="grid-input">
                  <InputMask
                    mask="9999 999 999"
                    value={get(victim, [VICTIM.TELEPHONE_NUMBER])}
                    disabled={props.disableInput}
                  >
                    {() => (
                      <TextField
                        label={translate(
                          { ...messages.victimTelephone },
                          props,
                        )}
                        fullWidth
                      />
                    )}
                  </InputMask>
                </Grid>
                <Grid item xs={3} className="grid-input">
                  <FormControl fullWidth>
                    <InputLabel
                      ref={inputLabel}
                      id="simple-select-outlined-label-procedure"
                      shrink={props.inputLabelProps}
                    >
                      {translate({ ...messages.victimQualityKey }, props)}
                    </InputLabel>
                    <StyledSelect
                      name={VICTIM.QUALITY_KEY}
                      value={get(victim, [VICTIM.QUALITY_KEY], '')}
                      disabled={props.disableInput}
                    >
                      {!isEmpty(get(props.listings, 'qualityOfVictim', [])) &&
                        props.listings!.qualityOfVictim.map(
                          (classItem: any) => (
                            <MenuItem
                              value={classItem.ItemKey}
                              key={classItem.ItemKey}
                            >
                              {classItem.ItemDescription}
                            </MenuItem>
                          ),
                        )}
                    </StyledSelect>
                  </FormControl>
                </Grid>
                {featureService.hasFeature(FEATURES.EVOLUTION_P4) &&
                  get(victim, [VICTIM.QUALITY_KEY], '') === '2' && (
                    <Grid item xs={3} className="grid-input">
                      <FormControl fullWidth>
                        <InputLabel
                          ref={inputLabel}
                          id="simple-select-outlined-label-procedure"
                          shrink={props.inputLabelProps}
                        >
                          {translate({ ...messages.vehiclePassenger }, props)}
                        </InputLabel>
                        <StyledSelect
                          name={VICTIM.VEHICLE_PASSENGER_ID}
                          value={get(victim, [VICTIM.VEHICLE_PASSENGER_ID], '')}
                          disabled={props.disableInput}
                        >
                          {get(props.listings, 'vehiclePassenger', []).map(
                            (classItem: any) => (
                              <MenuItem value={classItem.id} key={classItem.id}>
                                {classItem.title}
                              </MenuItem>
                            ),
                          )}
                        </StyledSelect>
                      </FormControl>
                    </Grid>
                  )}

                <Grid item xs={3} className="grid-input">
                  <FormControl fullWidth>
                    <InputLabel
                      ref={inputLabel}
                      id="simple-select-outlined-label-class"
                      shrink={props.inputLabelProps}
                    >
                      {translate({ ...messages.class }, props)}
                    </InputLabel>
                    <StyledSelect
                      name={VICTIM.CLASS_KEY}
                      value={get(victim, [VICTIM.CLASS_KEY])}
                      disabled={props.disableInput}
                      labelWidth={130}
                    >
                      {!isEmpty(get(props.listings, 'classVictim', [])) &&
                        props.listings!.classVictim.map((classItem: any) => (
                          <MenuItem
                            value={classItem.ItemKey}
                            key={classItem.ItemKey}
                          >
                            {classItem.ItemDescription}
                          </MenuItem>
                        ))}
                    </StyledSelect>
                  </FormControl>
                </Grid>
                <Grid item xs={3} className="grid-input">
                  <FormControl fullWidth>
                    <InputLabel
                      ref={inputLabel}
                      id="simple-select-outlined-label-procedure"
                      shrink={props.inputLabelProps}
                    >
                      {translate({ ...messages.typeProcedure }, props)}
                    </InputLabel>
                    <StyledSelect
                      name={VICTIM.PROCEDURE_ID}
                      value={get(victim, [VICTIM.PROCEDURE_ID], '')}
                      disabled={props.disableInput}
                    >
                      {!isEmpty(get(props.listings, 'procedures', [])) &&
                        filterVisibleOnly(props.listings!.procedures).map(
                          procedure => (
                            <MenuItem value={procedure.id} key={procedure.id}>
                              {procedure.title}
                            </MenuItem>
                          ),
                        )}
                    </StyledSelect>
                  </FormControl>
                </Grid>
                <Grid item xs={3} className="grid-input">
                  <FormControl fullWidth>
                    <AutoComplete
                      fullWidth
                      options={get(props.listings, 'Profession', [])}
                      getOptionLabel={(option: any) =>
                        `${
                          option?.ItemDescription ? option?.ItemDescription : ''
                        }`
                      }
                      value={
                        (get(props.listings, 'Profession', []).find(
                          (item: any) =>
                            item.ItemKey ===
                            get(victim, VICTIM.PROFESSION_TYPE),
                        ) || {}) as any
                      }
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          variant="standard"
                          label={translate(messages.professionType, props)}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                {isOtherProfession(
                  get(props.listings, 'Profession', []),
                  get(victim, [VICTIM.PROFESSION_TYPE]),
                ) && (
                  <Grid item xs={3}>
                    <TextField
                      label={translate({ ...messages.profession }, props)}
                      fullWidth
                      InputProps={props.inputProps}
                      disabled={props.disableInput}
                      name={VICTIM.PROFESSION}
                      value={get(victim, [VICTIM.PROFESSION])}
                      onChange={() => {}}
                      InputLabelProps={{
                        shrink: props.inputLabelProps,
                      }}
                    ></TextField>
                  </Grid>
                )}
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel
                      ref={inputLabel}
                      id="simple-select-outlined-label-work-accident"
                      shrink={props.inputLabelProps}
                    >
                      {translate({ ...messages.workAccident }, props)}
                    </InputLabel>
                    <StyledSelect
                      name={VICTIM.WORK_ACCIDENT_ID}
                      value={get(victim, [VICTIM.WORK_ACCIDENT_ID], '')}
                      disabled={props.disableInput}
                      labelWidth={140}
                    >
                      {!isEmpty(get(props.listings, 'workAccidents', [])) &&
                        props.listings!.workAccidents.map((type: any) => (
                          <MenuItem value={type.id} key={type.id}>
                            {type.title}
                          </MenuItem>
                        ))}
                    </StyledSelect>
                  </FormControl>
                </Grid>
                {get(victim, [VICTIM.INJURY_POST_KEY], '') !== '' &&
                  !INJURY_POST.DECEASED.includes(
                    get(victim, [VICTIM.INJURY_POST_KEY], ''),
                  ) && (
                    <>
                      <Grid item xs={3} className="grid-input">
                        <FormControl fullWidth>
                          <InputLabel
                            ref={inputLabel}
                            id="simple-select-outlined-label-procedure"
                            shrink={props.inputLabelProps}
                          >
                            {translate({ ...messages.availabilityItt }, props)}
                          </InputLabel>
                          <StyledSelect
                            name={VICTIM.ITT_AVAILABILITY}
                            value={
                              !isNil(get(victim, [VICTIM.ITT], null)) &&
                              get(victim, [VICTIM.ITT]) !== ''
                                ? VICTIM_ITT_AVAILABILITY.AVAILABLE
                                : VICTIM_ITT_AVAILABILITY.NOT_AVAILABLE
                            }
                            onChange={() => {}}
                            disabled={props.disableInput}
                          >
                            <MenuItem
                              value={VICTIM_ITT_AVAILABILITY.AVAILABLE}
                              key={uniqueId()}
                            >
                              {translate({ ...messages.available }, props)}
                            </MenuItem>
                            <MenuItem
                              value={VICTIM_ITT_AVAILABILITY.NOT_AVAILABLE}
                              key={uniqueId()}
                            >
                              {translate({ ...messages.notAvailable }, props)}
                            </MenuItem>
                          </StyledSelect>
                        </FormControl>
                      </Grid>

                      {!isNil(get(victim, [VICTIM.ITT], null)) &&
                        get(victim, [VICTIM.ITT]) !== '' && (
                          <Grid item xs={3}>
                            <TextField
                              label={translate({ ...messages.ITT }, props)}
                              type="number"
                              disabled={props.disableInput}
                              name={VICTIM.ITT}
                              value={parseInt(
                                (get(victim, [
                                  VICTIM.ITT,
                                ]) as unknown) as string,
                              )}
                              InputLabelProps={{
                                shrink: props.inputLabelProps,
                              }}
                              fullWidth
                            />
                          </Grid>
                        )}
                    </>
                  )}
                <Grid item xs={3} className="grid-input">
                  <FormControl fullWidth>
                    <InputLabel
                      ref={inputLabel}
                      id="simple-select-outlined-label-work-accident"
                      shrink={props.inputLabelProps}
                    >
                      {translate({ ...messages.victimOpposition }, props)}
                    </InputLabel>
                    <StyledSelect
                      name={VICTIM.OPPOSITION}
                      value={oppositionVictimValue(victim)}
                      disabled={props.disableInput}
                      labelWidth={140}
                    >
                      <MenuItem value={0} key={uniqueId('oppos_')}>
                        {translate({ ...messages.claimDeclarationNo }, props)}
                      </MenuItem>
                      <MenuItem value={1} key={uniqueId()}>
                        {translate({ ...messages.claimDeclarationYes }, props)}
                      </MenuItem>
                    </StyledSelect>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <Autocomplete
                    id="standard-basic-2"
                    options={getMoroccanCities(
                      get(props.listings, 'villes', []),
                    )}
                    getOptionLabel={city => city.ItemDescription}
                    autoSelect={true}
                    value={getCity(
                      get(props.listings, 'villes', []),
                      get(victim, [VICTIM.CITY]),
                    )}
                    filterOptions={filterOptions}
                    renderInput={params => (
                      <TextField {...params} name={VICTIM.CITY} />
                    )}
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    label={translate({ ...messages.adress }, props)}
                    InputProps={props.inputProps}
                    disabled={props.disableInput}
                    name={VICTIM.ADDRESS}
                    className="input-2-3"
                    value={get(victim, [VICTIM.ADDRESS])}
                    InputLabelProps={{
                      shrink: props.inputLabelProps,
                    }}
                  ></TextField>
                </Grid>
                {featureService.hasFeature(FEATURES.DECEASED_VICTIMS) &&
                  INJURY_POST.DECEASED.includes(
                    get(victim, [VICTIM.INJURY_POST_KEY], ''),
                  ) && (
                    <>
                      <Grid item xs={3}>
                        <TextField
                          type="number"
                          label={translate(
                            { ...messages.numberOfPartners },
                            props,
                          )}
                          className="input-2-3"
                          InputProps={{
                            inputProps:
                              get(victim, [VICTIM.GENDER], '') === GENDER.FEMALE
                                ? {
                                    ...props.inputProps,
                                    max: 1,
                                    min: 0,
                                  }
                                : props.inputProps,
                          }}
                          disabled={
                            props.disableInput ||
                            get(victim, [VICTIM.CLASS_KEY]) === MINOR_VALUE
                          }
                          name={VICTIM.NUMBER_OF_SPOUSE}
                          value={get(victim, [VICTIM.NUMBER_OF_SPOUSE])}
                          onChange={() => {}}
                          InputLabelProps={{
                            shrink: props.inputLabelProps,
                          }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          label={translate(
                            { ...messages.numberOfChildren },
                            props,
                          )}
                          type="number"
                          className="input-4"
                          InputProps={props.inputProps}
                          disabled={
                            props.disableInput ||
                            get(victim, [VICTIM.CLASS_KEY]) === MINOR_VALUE
                          }
                          name={VICTIM.NUMBER_OF_CHILDREN}
                          value={get(victim, [VICTIM.NUMBER_OF_CHILDREN])}
                          onChange={() => {}}
                          InputLabelProps={{
                            shrink: props.inputLabelProps,
                          }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          type="number"
                          label={translate(
                            { ...messages.numberOfDependents },
                            props,
                          )}
                          className="input-4-1"
                          InputProps={props.inputProps}
                          disabled={
                            props.disableInput ||
                            get(victim, [VICTIM.CLASS_KEY]) === MINOR_VALUE
                          }
                          name={VICTIM.NUMBER_OF_DEPENDENTS}
                          value={get(victim, [VICTIM.NUMBER_OF_DEPENDENTS])}
                          onChange={() => {}}
                          InputLabelProps={{
                            shrink: props.inputLabelProps,
                          }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          label={translate(
                            { ...messages.annualOfIncome },
                            props,
                          )}
                          type="number"
                          className="input-4-1"
                          InputProps={props.inputProps}
                          disabled={props.disableInput}
                          name={VICTIM.ANNUAL_INCOME}
                          value={get(victim, [VICTIM.ANNUAL_INCOME])}
                          onChange={() => {}}
                          InputLabelProps={{
                            shrink: props.inputLabelProps,
                          }}
                        />
                      </Grid>
                    </>
                  )}
              </Grid>
              {/* deceased victims */}
              {featureService.hasFeature(FEATURES.DECEASED_VICTIMS) &&
                INJURY_POST.DECEASED.includes(
                  get(victim, [VICTIM.INJURY_POST_KEY], ''),
                ) && (
                  <div className="deceaseBlock">
                    <Typography className="text deceased-title">
                      {translate({ ...messages.vitimDeceasedTitle }, props)}

                      {props.couldAddBeneficiaries && !props.isConsultation && (
                        <PrimaryButton
                          data-testid="add_beneficiary_btn"
                          className="add-beneficiary-btn"
                          onClick={() => {
                            setOpenDrawerAddBeneficiary(true);
                          }}
                          title={translate(
                            { ...messages.addBeneficiaryBtn },
                            props,
                          )}
                        />
                      )}
                    </Typography>

                    {victim.beneficiaries &&
                      victim.beneficiaries.map(
                        (
                          beneficiary: BeneficiaryType,
                          beneficiary_index: number,
                        ) => {
                          return (
                            <div
                              key={beneficiary_index}
                              className="beneficiary"
                            >
                              <BeneficiaryAccordion
                                listing={props.listings}
                                beneficiary={(beneficiary as unknown) as any}
                                index={beneficiary_index}
                                isDrawer={false}
                                inputProps={props.inputProps}
                                disableInput
                                inputLabelProps={props.inputLabelProps}
                                editButtonTitle={undefined}
                                handleBeneficiaryChange={() => {}}
                                handleSubmitEdit={undefined}
                                couldDelete={false}
                                handleDelete={() => {}}
                                victim={victim}
                                lossDate={(props.lossDate as unknown) as string}
                              />
                            </div>
                          );
                        },
                      )}
                  </div>
                )}
              {/*End deceased victims*/}
              <div className="checkdiv">
                <Typography className="text">
                  {translate({ ...messages.excludeVictim }, props)}
                </Typography>
                <FormControl>
                  <RadioGroup
                    row
                    value={isExcludedValue(
                      get(victim, [VICTIM.IS_EXCLUDED], ''),
                    )}
                  >
                    <FormControlLabel
                      control={
                        <Radio
                          color="primary"
                          value={CHOICE.YES}
                          checkedIcon={<CheckCircle />}
                          icon={<RadioButtonUnchecked />}
                          disabled={props.disableInput}
                        />
                      }
                      label={translate({ ...messages.yes }, props)}
                    />
                    <FormControlLabel
                      control={
                        <Radio
                          color="primary"
                          value={CHOICE.NO}
                          checkedIcon={<CheckCircle />}
                          icon={<RadioButtonUnchecked />}
                          disabled={props.disableInput}
                        />
                      }
                      label={translate({ ...messages.no }, props)}
                    />
                  </RadioGroup>
                </FormControl>
              </div>
            </div>
          </CustomAccordion>
        </div>
      </div>
      {openDrawerAddBeneficiary && (
        <BeneficiaryDrawer
          open={openDrawerAddBeneficiary}
          listing={props.listings}
          handleSubmit={handleSubmitAddBeneficiary}
          handleToggleDrawer={handleToggleDrawer}
          beneficiary={({} as unknown) as BeneficiaryType}
          victim={victim}
          isCourtMonitoringManagerUpdate={false}
          setBeneficiaryToUpdate={setBeneficiaryToAdd}
          beneficiaryToUpdate={beneficiaryToAdd}
          lossDate={(props.lossDate as unknown) as string}
          isCourtManager={false}
        />
      )}
    </Container>
  );
};
export default injectIntl(VictimCard);
